import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import type { Profile } from "../types/crm";
import { AuthError, mapAuthError, reportTechnicalError } from "../lib/errors";
import { queryClient } from "../lib/queryClient";
import { supabase } from "../lib/supabase";
import { resolveProfileResult, signInWithPassword } from "./authService";

interface AuthContextValue {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  error: AuthError | null;
  demoMode: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  retryProfile: () => Promise<void>;
}

const demoProfile: Profile = {
  id: "demo-user",
  email: "demo@yky.local",
  full_name: "演示管理员",
  role: "super_admin",
  team_id: "demo-team",
  is_active: true,
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
  client = supabase,
}: {
  children: ReactNode;
  client?: SupabaseClient | null;
}) {
  const demoMode = !client;
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(demoMode ? demoProfile : null);
  const [loading, setLoading] = useState(!demoMode);
  const [error, setError] = useState<AuthError | null>(null);
  const mountedRef = useRef(true);
  const requestRef = useRef(0);

  const fetchProfile = useCallback(async (nextSession: Session) => {
    if (!client) return demoProfile;
    const result = await client
      .from("profiles")
      .select("id,email,full_name,role,team_id,is_active")
      .eq("id", nextSession.user.id)
      .limit(2);
    return resolveProfileResult({ data: result.data, error: result.error });
  }, [client]);

  const applySession = useCallback(async (nextSession: Session | null) => {
    const requestId = ++requestRef.current;
    if (!mountedRef.current) return;
    setSession(nextSession);
    setError(null);
    if (!nextSession) {
      setProfile(demoMode ? demoProfile : null);
      return;
    }
    try {
      const nextProfile = await fetchProfile(nextSession);
      if (!mountedRef.current || requestId !== requestRef.current) return;
      setProfile(nextProfile);
    } catch (unknownError) {
      const authError = unknownError instanceof AuthError
        ? unknownError
        : mapAuthError(unknownError, "PROFILE_LOAD_FAILED");
      if (mountedRef.current && requestId === requestRef.current) {
        setProfile(null);
        setError(authError);
      }
      throw authError;
    }
  }, [demoMode, fetchProfile]);

  const runSessionUpdate = useCallback(async (
    nextSession: Session | null,
    context: string,
  ) => {
    if (!mountedRef.current) return;
    setLoading(true);
    try {
      await applySession(nextSession);
    } catch (unknownError) {
      reportTechnicalError(unknownError, context);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    mountedRef.current = true;
    if (!client) {
      setLoading(false);
      return () => {
        mountedRef.current = false;
      };
    }

    void (async () => {
      try {
        const { data, error: sessionError } = await client.auth.getSession();
        if (sessionError) throw mapAuthError(sessionError, "AUTH_REQUIRED");
        await applySession(data.session);
      } catch (unknownError) {
        const authError = unknownError instanceof AuthError
          ? unknownError
          : mapAuthError(unknownError, "AUTH_REQUIRED");
        if (mountedRef.current) {
          setSession(null);
          setProfile(null);
          setError(authError);
        }
        reportTechnicalError(authError, "auth.initialize");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    const { data: subscription } = client.auth.onAuthStateChange((event, nextSession) => {
      void runSessionUpdate(nextSession, "auth.onAuthStateChange." + event);
    });

    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      subscription.subscription.unsubscribe();
    };
  }, [applySession, client, runSessionUpdate]);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!client) {
      setProfile(demoProfile);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const nextSession = await signInWithPassword(client.auth, email, password);
      await applySession(nextSession);
    } catch (unknownError) {
      const authError = unknownError instanceof AuthError
        ? unknownError
        : mapAuthError(unknownError);
      setError(authError);
      reportTechnicalError(authError, "auth.signIn");
      throw authError;
    } finally {
      setLoading(false);
    }
  }, [applySession, client]);

  const signOut = useCallback(async () => {
    try {
      if (client) {
        const { error: signOutError } = await client.auth.signOut();
        if (signOutError) throw signOutError;
      }
    } catch (unknownError) {
      reportTechnicalError(unknownError, "auth.signOut");
    } finally {
      requestRef.current += 1;
      queryClient.clear();
      setSession(null);
      setProfile(demoMode ? demoProfile : null);
      setError(null);
      setLoading(false);
    }
  }, [client, demoMode]);

  const retryProfile = useCallback(async () => {
    if (!client) {
      setError(null);
      setProfile(demoProfile);
      return;
    }
    setLoading(true);
    try {
      const { data, error: sessionError } = await client.auth.getSession();
      if (sessionError) throw mapAuthError(sessionError, "AUTH_REQUIRED");
      if (!data.session) {
        throw new AuthError({
          code: "SESSION_EXPIRED",
          userMessage: "登录状态已过期，请重新登录",
          technicalMessage: "retryProfile found no session",
          status: 401,
          retryable: false,
        });
      }
      await applySession(data.session);
    } catch (unknownError) {
      const authError = unknownError instanceof AuthError
        ? unknownError
        : mapAuthError(unknownError, "PROFILE_LOAD_FAILED");
      setError(authError);
      reportTechnicalError(authError, "auth.retryProfile");
    } finally {
      setLoading(false);
    }
  }, [applySession, client]);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    profile,
    loading,
    error,
    demoMode,
    signIn,
    signOut,
    retryProfile,
  }), [session, profile, loading, error, demoMode, signIn, signOut, retryProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return context;
}

